import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Plus, Edit3, Calendar, CheckSquare, Filter, Search, ArrowRight, ArrowLeft, Save, X, Building, Layers, MapPin, Wrench, FileText, BarChart3, PanelRightClose, PanelRightOpen } from 'lucide-react';

// Construction stage mapping based on Excel categories
const CONSTRUCTION_STAGES = {
  'Earthwork': { order: 1, color: 'bg-amber-100 text-amber-800' },
  'Site Preparation': { order: 2, color: 'bg-orange-100 text-orange-800' },
  'RCC': { order: 3, color: 'bg-blue-100 text-blue-800' },
  'Masonry': { order: 4, color: 'bg-red-100 text-red-800' },
  'Waterproofing': { order: 5, color: 'bg-cyan-100 text-cyan-800' },
  'Plaster Internal': { order: 6, color: 'bg-green-100 text-green-800' },
  'Electrical': { order: 7, color: 'bg-yellow-100 text-yellow-800' },
  'Plumbing': { order: 8, color: 'bg-indigo-100 text-indigo-800' },
  'Doors & Windows': { order: 9, color: 'bg-purple-100 text-purple-800' },
  'Painting Internal': { order: 10, color: 'bg-pink-100 text-pink-800' },
  'Flooring & Tiling': { order: 11, color: 'bg-emerald-100 text-emerald-800' },
  'False Ceiling': { order: 12, color: 'bg-teal-100 text-teal-800' },
  'Painting External': { order: 13, color: 'bg-lime-100 text-lime-800' },
  'External Plaster': { order: 14, color: 'bg-slate-100 text-slate-800' },
  'Fabrication': { order: 15, color: 'bg-zinc-100 text-zinc-800' },
  'Services/Utilities': { order: 16, color: 'bg-stone-100 text-stone-800' },
  'Painting': { order: 17, color: 'bg-rose-100 text-rose-800' },
  'Departmental': { order: 18, color: 'bg-violet-100 text-violet-800' },
  'Housekeeping Work': { order: 19, color: 'bg-fuchsia-100 text-fuchsia-800' }
};

// Utility functions for planning
const calculateVariance = (originalDate, revisedDate) => {
  if (!revisedDate) return { days: 0, status: 'on-track' };
  
  const original = new Date(originalDate);
  const revised = new Date(revisedDate);
  const diffTime = revised - original;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  let status = 'on-track';
  if (diffDays > 14) status = 'delayed';
  else if (diffDays > 3) status = 'at-risk';
  else if (diffDays < 0) status = 'ahead';
  
  return { days: diffDays, status };
};

const getVarianceColor = (status) => {
  const colors = {
    'on-track': 'text-green-600 bg-green-50',
    'at-risk': 'text-yellow-600 bg-yellow-50',
    'delayed': 'text-red-600 bg-red-50',
    'ahead': 'text-blue-600 bg-blue-50'
  };
  return colors[status] || colors['on-track'];
};

// Enhanced mock data structure with planning levels
const mockMasterPlan = [
  {
    id: '1304675',
    taskName: 'Excavation in Soft Soil/Murum',
    category: 'Earthwork',
    subCategory: 'Excavation',
    building: 'C Building',
    floor: 'N/A',
    area: 'Foundation',
    unit: 'Cum',
    quantity: 8796.35,
    estimatedAmount: 1435589.3,
    quantityCompleted: 8795.47,
    actualCost: 2102721.78,
    status: 'Started',
    // Original schedule (baseline)
    originalSchedule: {
      startDate: '2024-01-15',
      finishDate: '2024-02-15',
      duration: 31
    },
    // Monthly revision
    monthlyRevision: {
      startDate: '2024-02-01',
      finishDate: '2024-03-05',
      reason: 'Weather delays and equipment shortage',
      revisedBy: 'Supervisor',
      revisedAt: '2024-02-01'
    },
    // Weekly revision
    weeklyRevision: {
      startDate: '2024-02-05',
      finishDate: '2024-03-10',
      reason: 'Site access issues',
      notes: 'Additional time needed for site preparation',
      revisedBy: 'Site Engineer',
      revisedAt: '2024-02-05',
      progressTarget: 60,
      quantityTarget: 5200
    },
    assignedTo: 'John Smith',
    contractor: 'ABC Contractors',
    priority: 'High'
  },
  {
    id: '1371582',
    taskName: 'Excavation in Hard Rock',
    category: 'Earthwork',
    subCategory: 'Excavation',
    building: 'C Building',
    floor: 'N/A',
    area: 'Foundation',
    unit: 'Cum',
    quantity: 3099.31,
    estimatedAmount: 2695725,
    quantityCompleted: 3099.00,
    actualCost: 2461832.82,
    status: 'Completed',
    originalSchedule: {
      startDate: '2024-01-20',
      finishDate: '2024-02-20',
      duration: 31
    },
    monthlyRevision: {
      startDate: '2024-01-25',
      finishDate: '2024-02-18',
      reason: 'Equipment optimization',
      revisedBy: 'Supervisor',
      revisedAt: '2024-01-25'
    },
    assignedTo: 'Sarah Johnson',
    contractor: 'ABC Contractors',
    priority: 'High'
  },
  {
    id: '1393099',
    taskName: 'PCC Pour 1 Shuttering',
    category: 'RCC',
    subCategory: 'Shuttering',
    building: 'C Building',
    floor: 'Basement',
    area: 'Foundation',
    unit: 'Sqm',
    quantity: 13.674,
    estimatedAmount: 2502.342,
    quantityCompleted: 13.67,
    actualCost: 0,
    status: 'Planned',
    originalSchedule: {
      startDate: '2024-03-01',
      finishDate: '2024-03-15',
      duration: 14
    },
    assignedTo: 'Mike Chen',
    contractor: 'PQR Contractors',
    priority: 'Medium'
  },
  {
    id: '1393101',
    taskName: 'PCC Pour 1 RMC-M10',
    category: 'RCC',
    subCategory: 'Concrete',
    building: 'C Building',
    floor: 'Basement',
    area: 'Foundation',
    unit: 'Cum',
    quantity: 100.3,
    estimatedAmount: 438695.313,
    quantityCompleted: 100.29,
    actualCost: 457427.45,
    status: 'Completed',
    originalSchedule: {
      startDate: '2024-02-01',
      finishDate: '2024-02-28',
      duration: 27
    },
    monthlyRevision: {
      startDate: '2024-02-05',
      finishDate: '2024-03-10',
      reason: 'Material delivery delays',
      revisedBy: 'Supervisor',
      revisedAt: '2024-02-03'
    },
    weeklyRevision: {
      startDate: '2024-02-08',
      finishDate: '2024-03-15',
      reason: 'Weather conditions',
      notes: 'Extended curing time due to rain',
      revisedBy: 'Site Engineer',
      revisedAt: '2024-02-07',
      progressTarget: 85,
      quantityTarget: 85
    },
    assignedTo: 'Lisa Anderson',
    contractor: 'XYZ Contractors',
    priority: 'High'
  },
  {
    id: '1413093',
    taskName: 'PCC Pour 1 Curing Compound',
    category: 'RCC',
    subCategory: 'Curing',
    building: 'C Building',
    floor: 'Basement',
    area: 'Foundation',
    unit: 'Sqm',
    quantity: 13.674,
    estimatedAmount: 121.26,
    quantityCompleted: 13.67,
    actualCost: 121.26,
    status: 'In Progress',
    startDate: '2024-03-05',
    finishDate: '2024-03-10',
    assignedTo: 'Mike Chen',
    contractor: 'PQR Contractors',
    priority: 'Medium'
  },
  {
    id: '1393102',
    taskName: 'Column Reinforcement',
    category: 'RCC',
    subCategory: 'Reinforcement',
    building: 'A Building',
    floor: 'Ground',
    area: 'Structure',
    unit: 'Kg',
    quantity: 2500.5,
    estimatedAmount: 125000,
    quantityCompleted: 1200.25,
    actualCost: 65000,
    status: 'In Progress',
    startDate: '2024-03-10',
    finishDate: '2024-04-10',
    assignedTo: 'David Kumar',
    contractor: 'Steel Works Ltd',
    priority: 'High'
  },
  {
    id: '1393103',
    taskName: 'Block Work',
    category: 'Masonry',
    subCategory: 'Block Work',
    building: 'B Building',
    floor: '1st Floor',
    area: 'Walls',
    unit: 'Sqm',
    quantity: 850.75,
    estimatedAmount: 340300,
    quantityCompleted: 0,
    actualCost: 0,
    status: 'Planned',
    startDate: '2024-04-15',
    finishDate: '2024-05-15',
    assignedTo: 'Robert Wilson',
    contractor: 'Mason Contractors',
    priority: 'Medium'
  },
  {
    id: '1393104',
    taskName: 'Beam Shuttering',
    category: 'RCC',
    subCategory: 'Shuttering',
    building: 'A Building',
    floor: '2nd Floor',
    area: 'Structure',
    unit: 'Sqm',
    quantity: 420.5,
    estimatedAmount: 84100,
    quantityCompleted: 150.2,
    actualCost: 30040,
    status: 'In Progress',
    startDate: '2024-03-20',
    finishDate: '2024-04-05',
    assignedTo: 'Mike Chen',
    contractor: 'PQR Contractors',
    priority: 'High'
  },
  {
    id: '1393105',
    taskName: 'Slab Reinforcement',
    category: 'RCC',
    subCategory: 'Reinforcement',
    building: 'B Building',
    floor: '3rd Floor',
    area: 'Structure',
    unit: 'Kg',
    quantity: 3200.75,
    estimatedAmount: 160037.5,
    quantityCompleted: 0,
    actualCost: 0,
    status: 'Planned',
    startDate: '2024-04-20',
    finishDate: '2024-05-10',
    assignedTo: 'David Kumar',
    contractor: 'Steel Works Ltd',
    priority: 'Medium'
  },
  {
    id: '1393106',
    taskName: 'Site Utilities Installation',
    category: 'MEP',
    subCategory: 'Electrical',
    building: 'Common Areas',
    floor: 'N/A',
    area: 'Site Wide',
    unit: 'LS',
    quantity: 1,
    estimatedAmount: 250000,
    quantityCompleted: 0.5,
    actualCost: 125000,
    status: 'In Progress',
    startDate: '2024-02-01',
    finishDate: '2024-06-30',
    assignedTo: 'John Smith',
    contractor: 'Electrical Contractors',
    priority: 'High'
  }
];

const ConstructionPlanningInterface = () => {
  const [currentView, setCurrentView] = useState('master'); // master, monthly, weekly
  const [masterPlan, setMasterPlan] = useState(mockMasterPlan);
  const [monthlyPlan, setMonthlyPlan] = useState([]);
  const [weeklyPlan, setWeeklyPlan] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedNode, setSelectedNode] = useState('root'); // Controls what's shown in table
  const [expandedNodes, setExpandedNodes] = useState({ 'root': true });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('All');
  const [filterEngineer, setFilterEngineer] = useState('All');
  const [filterContractor, setFilterContractor] = useState('All');
  const [filterFloor, setFilterFloor] = useState('All');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [breadcrumb, setBreadcrumb] = useState(['All Buildings']);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Build hierarchical tree structure with BUILDINGS at TOP LEVEL
  const buildTreeStructure = (tasks) => {
    const tree = {};
    
    tasks.forEach(task => {
      // CORRECT HIERARCHY: Building → Category → SubCategory → Area
      const path = [task.building, task.category, task.subCategory, task.area];
      let current = tree;
      
      path.forEach((segment, index) => {
        if (!current[segment]) {
          current[segment] = {
            type: ['building', 'category', 'subCategory', 'area'][index],
            name: segment,
            children: index === path.length - 1 ? [] : {},
            tasks: [],
            stats: { total: 0, completed: 0, inProgress: 0, planned: 0, estimatedCost: 0, actualCost: 0 }
          };
        }
        
        if (index === path.length - 1) {
          current[segment].tasks.push(task);
        } else {
          current = current[segment].children;
        }
      });
    });
    
    // Calculate statistics
    const calculateStats = (node) => {
      if (node.tasks && node.tasks.length > 0) {
        node.stats = node.tasks.reduce((acc, task) => ({
          total: acc.total + 1,
          completed: acc.completed + (task.status === 'Completed' ? 1 : 0),
          inProgress: acc.inProgress + (task.status === 'In Progress' || task.status === 'Started' ? 1 : 0),
          planned: acc.planned + (task.status === 'Planned' ? 1 : 0),
          estimatedCost: acc.estimatedCost + task.estimatedAmount,
          actualCost: acc.actualCost + task.actualCost
        }), { total: 0, completed: 0, inProgress: 0, planned: 0, estimatedCost: 0, actualCost: 0 });
      }
      
      if (node.children && typeof node.children === 'object') {
        Object.values(node.children).forEach(calculateStats);
        
        node.stats = Object.values(node.children).reduce((acc, child) => ({
          total: acc.total + child.stats.total,
          completed: acc.completed + child.stats.completed,
          inProgress: acc.inProgress + child.stats.inProgress,
          planned: acc.planned + child.stats.planned,
          estimatedCost: acc.estimatedCost + child.stats.estimatedCost,
          actualCost: acc.actualCost + child.stats.actualCost
        }), { total: 0, completed: 0, inProgress: 0, planned: 0, estimatedCost: 0, actualCost: 0 });
      }
    };
    
    Object.values(tree).forEach(calculateStats);
    return tree;
  };

  const getCurrentTasks = () => {
    switch (currentView) {
      case 'master': return masterPlan;
      case 'monthly': return monthlyPlan;
      case 'weekly': return weeklyPlan;
      default: return [];
    }
  };

  const treeStructure = useMemo(() => buildTreeStructure(getCurrentTasks()), [currentView, masterPlan, monthlyPlan, weeklyPlan]);

  // Get filtered tasks based on selected tree node
  const getFilteredTasksForTable = () => {
    const allTasks = getCurrentTasks();
    
    if (selectedNode === 'root') {
      return allTasks;
    }

    const nodePath = selectedNode.split('.');
    let currentNode = treeStructure;
    
    try {
      for (let i = 0; i < nodePath.length; i++) {
        currentNode = currentNode[nodePath[i]];
        if (i < nodePath.length - 1) {
          currentNode = currentNode.children;
        }
      }

      // If it's a leaf node with tasks, return those tasks
      if (currentNode.tasks && currentNode.tasks.length > 0) {
        return currentNode.tasks;
      }

      // If it's a parent node, collect all tasks from children
      const collectAllTasks = (node) => {
        let tasks = [...(node.tasks || [])];
        if (node.children && typeof node.children === 'object') {
          Object.values(node.children).forEach(child => {
            tasks = [...tasks, ...collectAllTasks(child)];
          });
        }
        return tasks;
      };

      return collectAllTasks(currentNode);
    } catch (error) {
      return allTasks;
    }
  };

  const toggleNodeExpansion = (nodeKey) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeKey]: !prev[nodeKey]
    }));
  };

  const selectNode = (nodeKey, nodePath) => {
    setSelectedNode(nodeKey);
    setBreadcrumb(nodePath);
  };

  // Editing functions
  const startEditing = (task) => {
    setEditingTask(task.id);
    setEditFormData({
      monthlyStartDate: task.monthlyRevision?.startDate || '',
      monthlyFinishDate: task.monthlyRevision?.finishDate || '',
      monthlyReason: task.monthlyRevision?.reason || '',
      weeklyStartDate: task.weeklyRevision?.startDate || '',
      weeklyFinishDate: task.weeklyRevision?.finishDate || '',
      weeklyReason: task.weeklyRevision?.reason || '',
      weeklyNotes: task.weeklyRevision?.notes || '',
      assignedTo: task.assignedTo,
      contractor: task.contractor
    });
  };

  const cancelEditing = () => {
    setEditingTask(null);
    setEditFormData({});
  };

  const saveEditing = () => {
    // Here you would typically save to backend
    console.log('Saving task:', editingTask, editFormData);
    
    // For now, just close the editing mode
    setEditingTask(null);
    setEditFormData({});
    
    // In a real app, you'd update the task data and refresh the view
  };

  const updateEditFormData = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getNodeIcon = (type) => {
    const icons = {
      building: Building,
      category: Wrench,
      subCategory: Layers,
      area: MapPin
    };
    return icons[type] || Layers;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Planned': 'bg-gray-100 text-gray-800',
      'Started': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const TreeNode = ({ nodeKey, node, level = 0, parentPath = [] }) => {
    const isExpanded = expandedNodes[nodeKey] || level === 0;
    const Icon = getNodeIcon(node.type);
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const hasTasks = node.tasks && node.tasks.length > 0;
    const completionRate = node.stats.total > 0 ? ((node.stats.completed / node.stats.total) * 100).toFixed(1) : 0;
    const currentPath = [...parentPath, node.name];
    const isSelected = selectedNode === nodeKey;

    return (
      <div className="select-none">
        <div 
          className={`flex items-center space-x-2 py-2 px-3 hover:bg-gray-100 cursor-pointer rounded-lg ${
            isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
          }`}
          style={{ marginLeft: `${level * 20}px` }}
          onClick={() => selectNode(nodeKey, currentPath)}
        >
          {(hasChildren || hasTasks) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNodeExpansion(nodeKey);
              }}
              className="p-1"
            >
              {isExpanded ? 
                <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                <ChevronRight className="w-4 h-4 text-gray-500" />
              }
            </button>
          )}
          
          <Icon className="w-4 h-4 text-gray-600" />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-gray-900 truncate">
                {node.name}
              </span>
              <div className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                {node.stats.total} tasks • {completionRate}%
              </div>
            </div>
            
            {node.stats.total > 0 && (
              <div className="flex items-center space-x-4 mt-1 text-xs">
                <span className="text-green-600">{node.stats.completed} done</span>
                <span className="text-yellow-600">{node.stats.inProgress} active</span>
                <span className="text-gray-500">{node.stats.planned} planned</span>
              </div>
            )}
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {Object.entries(node.children).map(([childKey, childNode]) => (
              <TreeNode 
                key={childKey}
                nodeKey={`${nodeKey}.${childKey}`}
                node={childNode}
                level={level + 1}
                parentPath={currentPath}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const TaskTable = () => {
    const filteredTasks = getFilteredTasksForTable();

    // Get unique engineers, contractors, and floors for filter dropdowns
    const allTasks = getCurrentTasks();
    const engineers = ['All', ...new Set(allTasks.map(task => task.assignedTo).filter(Boolean))];
    const contractors = ['All', ...new Set(allTasks.map(task => task.contractor).filter(Boolean))];
    const floors = ['All', ...new Set(allTasks.map(task => task.floor).filter(Boolean))];
    
    // Apply additional filters
    const searchedTasks = filteredTasks.filter(task => {
      // Text search
      const matchesSearch = task.taskName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          task.id.includes(searchTerm);
      
      // Stage filter
      const matchesStage = filterStage === 'All' || task.category === filterStage;
      
      // Engineer filter
      const matchesEngineer = filterEngineer === 'All' || task.assignedTo === filterEngineer;
      
      // Contractor filter  
      const matchesContractor = filterContractor === 'All' || task.contractor === filterContractor;
      
      // Floor filter
      const matchesFloor = filterFloor === 'All' || task.floor === filterFloor;
      
      // Date range filter
      let matchesDateRange = true;
      if (filterDateStart || filterDateEnd) {
        const taskStartDate = new Date(task.originalSchedule?.startDate || task.startDate);
        const taskEndDate = new Date(task.originalSchedule?.finishDate || task.finishDate);
        
        if (filterDateStart) {
          const filterStart = new Date(filterDateStart);
          matchesDateRange = matchesDateRange && taskEndDate >= filterStart;
        }
        
        if (filterDateEnd) {
          const filterEnd = new Date(filterDateEnd);
          matchesDateRange = matchesDateRange && taskStartDate <= filterEnd;
        }
      }
      
      return matchesSearch && matchesStage && matchesEngineer && matchesContractor && matchesFloor && matchesDateRange;
    });

    const toggleTaskSelection = (taskId) => {
      setSelectedTasks(prev => 
        prev.includes(taskId) 
          ? prev.filter(id => id !== taskId)
          : [...prev, taskId]
      );
    };

    const selectAllTasks = () => {
      const allTaskIds = searchedTasks.map(task => task.id);
      setSelectedTasks(prev => 
        prev.length === allTaskIds.length && allTaskIds.every(id => prev.includes(id))
          ? []
          : allTaskIds
      );
    };

    const isAllSelected = searchedTasks.length > 0 && 
      searchedTasks.every(task => selectedTasks.includes(task.id));

    return (
      <div className="flex flex-col h-full">
        {/* Table Header with Breadcrumb and Actions */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
            <FileText className="w-4 h-4" />
            <span>EKAM Project</span>
            {breadcrumb.map((crumb, index) => (
              <React.Fragment key={index}>
                <ChevronRight className="w-3 h-3" />
                <span className={index === breadcrumb.length - 1 ? 'font-medium text-gray-900' : ''}>
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Search Row */}
          <div className="flex items-center space-x-4 mb-3">
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>
            <div className="text-sm text-gray-500">
              {searchedTasks.length} tasks shown
            </div>
          </div>

          {/* Filter Controls Row */}
          <div className="flex items-center space-x-3 mb-3 p-2 bg-gray-50 rounded-lg flex-wrap">
            <span className="text-sm font-medium text-gray-700">Filters:</span>
            
            {/* Stage Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">🏗️ Stage:</label>
              <select 
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="All">All Stages</option>
                {Object.keys(CONSTRUCTION_STAGES).map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>
            
            {/* Engineer Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">Engineer:</label>
              <select 
                value={filterEngineer}
                onChange={(e) => setFilterEngineer(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {engineers.map(engineer => (
                  <option key={engineer} value={engineer}>
                    {engineer === 'All' ? 'All Engineers' : engineer}
                  </option>
                ))}
              </select>
            </div>

            {/* Contractor Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">Contractor:</label>
              <select 
                value={filterContractor}
                onChange={(e) => setFilterContractor(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {contractors.map(contractor => (
                  <option key={contractor} value={contractor}>
                    {contractor === 'All' ? 'All Contractors' : contractor}
                  </option>
                ))}
              </select>
            </div>

            {/* Floor Filter */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">Floor:</label>
              <select 
                value={filterFloor}
                onChange={(e) => setFilterFloor(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {floors.map(floor => (
                  <option key={floor} value={floor}>
                    {floor === 'All' ? 'All Floors' : floor === 'N/A' ? 'Site-wide' : floor}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center space-x-1">
              <label className="text-xs font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Clear Filters Button */}
            {(filterEngineer !== 'All' || filterContractor !== 'All' || filterFloor !== 'All' || filterDateStart || filterDateEnd || searchTerm) && (
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setFilterEngineer('All');
                  setFilterContractor('All');
                  setFilterFloor('All');
                  setFilterDateStart('');
                  setFilterDateEnd('');
                }}
                className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Action Buttons */}
          {selectedTasks.length > 0 && (
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-sm text-gray-600">
                {selectedTasks.length} selected
              </span>
              {currentView === 'master' && (
                <button 
                  onClick={() => {
                    const tasksToMove = masterPlan.filter(task => selectedTasks.includes(task.id));
                    const tasksWithTargets = tasksToMove.map(task => ({
                      ...task,
                      targetQuantity: task.quantity,
                      targetStartDate: task.startDate,
                      targetFinishDate: task.finishDate,
                      monthlyNotes: '',
                      addedToMonthly: new Date().toISOString().split('T')[0]
                    }));
                    setMonthlyPlan(prev => [...prev, ...tasksWithTargets]);
                    setSelectedTasks([]);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>Add to Monthly Plan</span>
                </button>
              )}
              {currentView === 'monthly' && (
                <button 
                  onClick={() => {
                    const tasksToMove = monthlyPlan.filter(task => selectedTasks.includes(task.id));
                    const tasksWithWeeklyTargets = tasksToMove.map(task => ({
                      ...task,
                      weeklyTargetQuantity: task.targetQuantity || task.quantity,
                      weeklyTargetStartDate: task.targetStartDate || task.startDate,
                      weeklyTargetFinishDate: task.targetFinishDate || task.finishDate,
                      weeklyNotes: '',
                      addedToWeekly: new Date().toISOString().split('T')[0]
                    }));
                    setWeeklyPlan(prev => [...prev, ...tasksWithWeeklyTargets]);
                    setSelectedTasks([]);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  <ArrowRight className="w-4 h-4" />
                  <span>Add to Weekly Plan</span>
                </button>
              )}
              {currentView !== 'master' && (
                <button className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm">
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {(filterEngineer !== 'All' || filterContractor !== 'All' || filterFloor !== 'All' || filterDateStart || filterDateEnd) && (
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
            <div className="flex items-center space-x-4 text-sm">
              <span className="font-medium text-blue-900">Active Filters:</span>
              {filterEngineer !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Engineer: {filterEngineer}
                </span>
              )}
              {filterContractor !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Contractor: {filterContractor}
                </span>
              )}
              {filterFloor !== 'All' && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Floor: {filterFloor === 'N/A' ? 'Site-wide' : filterFloor}
                </span>
              )}
              {(filterDateStart || filterDateEnd) && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  Date: {filterDateStart || 'start'} to {filterDateEnd || 'end'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="w-12 pl-4 pr-0 py-2 text-left align-top">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={selectAllTasks}
                    className="rounded mt-1"
                  />
                </th>
                <th className="w-64 pl-2 pr-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task
                </th>
                <th className="w-20 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stage
                </th>
                <th className="w-20 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Floor
                </th>
                <th className="w-20 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-24 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="w-24 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="w-32 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Original Schedule
                </th>
                <th className="w-32 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monthly Revision
                </th>
                <th className="w-32 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Weekly Revision
                </th>
                <th className="w-24 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variance
                </th>
                <th className="w-32 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignment
                </th>
                <th className="w-16 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {searchedTasks.map(task => {
                const completionRate = ((task.quantityCompleted / task.quantity) * 100).toFixed(1);
                
                // Calculate variances with fallbacks
                const originalFinishDate = task.originalSchedule?.finishDate || task.finishDate;
                
                const monthlyVariance = task.monthlyRevision 
                  ? calculateVariance(originalFinishDate, task.monthlyRevision.finishDate)
                  : { days: 0, status: 'on-track' };
                
                const weeklyVariance = task.weeklyRevision 
                  ? calculateVariance(
                      task.monthlyRevision?.finishDate || originalFinishDate, 
                      task.weeklyRevision.finishDate
                    )
                  : { days: 0, status: 'on-track' };
                
                const totalVariance = task.weeklyRevision 
                  ? calculateVariance(originalFinishDate, task.weeklyRevision.finishDate)
                  : monthlyVariance;

                const stageInfo = CONSTRUCTION_STAGES[task.category] || { order: 0, color: 'bg-gray-100 text-gray-800' };
                
                return (
                  <tr key={task.id} className="hover:bg-gray-50">
                    {/* Checkbox */}
                    <td className="pl-4 pr-0 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selectedTasks.includes(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        className="rounded mt-1"
                      />
                    </td>
                    
                    {/* Task */}
                    <td className="pl-2 pr-2 py-2">
                      <div className="font-medium text-sm text-gray-900 truncate">{task.taskName}</div>
                      <div className="text-xs text-gray-500 truncate">
                        ID: {task.id} • {task.subCategory}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {task.area}
                      </div>
                    </td>
                    
                    {/* Stage */}
                    <td className="px-2 py-2">
                      <span className={`px-1 py-0.5 rounded text-xs font-medium ${stageInfo.color}`}>
                        {task.category}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">
                        #{stageInfo.order}
                      </div>
                    </td>
                    
                    {/* Floor */}
                    <td className="px-2 py-2">
                      <div className="text-sm text-gray-900">
                        {task.floor === 'N/A' ? (
                          <span className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            Site
                          </span>
                        ) : (
                          <span className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                            {task.floor}
                          </span>
                        )}
                      </div>
                    </td>
                    
                    {/* Status */}
                    <td className="px-2 py-2">
                      <span className={`px-1 py-0.5 rounded text-xs font-medium ${getStatusBadge(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                    
                    {/* Progress */}
                    <td className="px-2 py-2">
                      <div className="text-xs text-gray-900">
                        {task.quantityCompleted} / {task.quantity}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full" 
                          style={{ width: `${Math.min(completionRate, 100)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{completionRate}%</div>
                    </td>
                    
                    {/* Cost */}
                    <td className="px-2 py-2 text-xs">
                      <div className="text-gray-900">₹{(task.estimatedAmount/100000).toFixed(1)}L</div>
                      <div className="text-xs text-gray-500">₹{(task.actualCost/100000).toFixed(1)}L</div>
                    </td>
                    
                    {/* Original Schedule */}
                    <td className="px-1 py-2 text-xs">
                      <div className="text-gray-900 font-medium">
                        {task.originalSchedule?.startDate || task.startDate}
                      </div>
                      <div className="text-gray-500">
                        {task.originalSchedule?.finishDate || task.finishDate}
                      </div>
                      <div className="text-xs text-gray-400">
                        {task.originalSchedule?.duration || 'N/A'}d
                      </div>
                    </td>
                    
                    {/* Monthly Revision */}
                    <td className="px-1 py-2 text-xs">
                      {editingTask === task.id && (currentView === 'monthly' || currentView === 'weekly') ? (
                        <div className="space-y-1">
                          <input
                            type="date"
                            value={editFormData.monthlyStartDate}
                            onChange={(e) => updateEditFormData('monthlyStartDate', e.target.value)}
                            className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                            placeholder="Start date"
                          />
                          <input
                            type="date"
                            value={editFormData.monthlyFinishDate}
                            onChange={(e) => updateEditFormData('monthlyFinishDate', e.target.value)}
                            className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                            placeholder="End date"
                          />
                          <select
                            value={editFormData.monthlyReason}
                            onChange={(e) => updateEditFormData('monthlyReason', e.target.value)}
                            className="w-full border border-blue-300 rounded px-1 py-0.5 text-xs"
                          >
                            <option value="">Select reason</option>
                            <option value="Weather delays">Weather delays</option>
                            <option value="Material shortage">Material shortage</option>
                            <option value="Equipment issues">Equipment issues</option>
                            <option value="Resource unavailability">Resource unavailability</option>
                            <option value="Site conditions">Site conditions</option>
                            <option value="Client changes">Client changes</option>
                          </select>
                        </div>
                      ) : task.monthlyRevision ? (
                        <>
                          <div className="text-blue-900 font-medium">
                            📝 {task.monthlyRevision.startDate}
                          </div>
                          <div className="text-blue-700">
                            {task.monthlyRevision.finishDate}
                          </div>
                          <div className="text-xs text-blue-500">
                            {monthlyVariance.days > 0 ? `+${monthlyVariance.days}d` : 
                             monthlyVariance.days < 0 ? `${monthlyVariance.days}d` : 'On time'}
                          </div>
                        </>
                      ) : (currentView === 'monthly' || currentView === 'weekly') ? (
                        <button 
                          onClick={() => startEditing(task)}
                          className="text-blue-600 hover:text-blue-800 italic text-xs"
                        >
                          + Set monthly plan
                        </button>
                      ) : (
                        <div className="text-gray-400 italic">Not set</div>
                      )}
                    </td>
                    
                    {/* Weekly Revision */}
                    <td className="px-1 py-2 text-xs">
                      {editingTask === task.id && currentView === 'weekly' ? (
                        <div className="space-y-1">
                          <input
                            type="date"
                            value={editFormData.weeklyStartDate}
                            onChange={(e) => updateEditFormData('weeklyStartDate', e.target.value)}
                            className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                            placeholder="Start date"
                          />
                          <input
                            type="date"
                            value={editFormData.weeklyFinishDate}
                            onChange={(e) => updateEditFormData('weeklyFinishDate', e.target.value)}
                            className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                            placeholder="End date"
                          />
                          <input
                            type="text"
                            value={editFormData.weeklyNotes}
                            onChange={(e) => updateEditFormData('weeklyNotes', e.target.value)}
                            className="w-full border border-green-300 rounded px-1 py-0.5 text-xs"
                            placeholder="Notes"
                          />
                        </div>
                      ) : task.weeklyRevision ? (
                        <>
                          <div className="text-green-900 font-medium">
                            📋 {task.weeklyRevision.startDate}
                          </div>
                          <div className="text-green-700">
                            {task.weeklyRevision.finishDate}
                          </div>
                          <div className="text-xs text-green-500">
                            {weeklyVariance.days > 0 ? `+${weeklyVariance.days}d` : 
                             weeklyVariance.days < 0 ? `${weeklyVariance.days}d` : 'On time'}
                          </div>
                        </>
                      ) : currentView === 'weekly' ? (
                        <button 
                          onClick={() => startEditing(task)}
                          className="text-green-600 hover:text-green-800 italic text-xs"
                        >
                          + Set weekly plan
                        </button>
                      ) : (
                        <div className="text-gray-400 italic">Not set</div>
                      )}
                    </td>
                    
                    {/* Total Variance */}
                    <td className="px-1 py-2 text-xs">
                      <div className={`px-2 py-1 rounded text-center font-medium ${getVarianceColor(totalVariance.status)}`}>
                        {totalVariance.days > 0 ? `+${totalVariance.days}` : totalVariance.days}d
                      </div>
                      <div className="text-xs text-gray-500 mt-1 capitalize">
                        {totalVariance.status.replace('-', ' ')}
                      </div>
                    </td>
                    
                    {/* Assignment */}
                    <td className="px-2 py-2 text-xs">
                      {editingTask === task.id ? (
                        <div className="space-y-1">
                          <select
                            value={editFormData.assignedTo}
                            onChange={(e) => updateEditFormData('assignedTo', e.target.value)}
                            className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                          >
                            {engineers.filter(eng => eng !== 'All').map(engineer => (
                              <option key={engineer} value={engineer}>{engineer}</option>
                            ))}
                          </select>
                          <select
                            value={editFormData.contractor}
                            onChange={(e) => updateEditFormData('contractor', e.target.value)}
                            className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                          >
                            {contractors.filter(cont => cont !== 'All').map(contractor => (
                              <option key={contractor} value={contractor}>{contractor}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <>
                          <div className="text-gray-900 truncate">
                            👷 {task.assignedTo}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            🏗️ {task.contractor}
                          </div>
                        </>
                      )}
                    </td>
                    
                    {/* Actions */}
                    <td className="px-2 py-2">
                      {editingTask === task.id ? (
                        <div className="flex space-x-1">
                          <button 
                            onClick={saveEditing}
                            className="p-1 hover:bg-green-100 rounded text-green-600"
                            title="Save"
                          >
                            <Save className="w-3 h-3" />
                          </button>
                          <button 
                            onClick={cancelEditing}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                            title="Cancel"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (currentView === 'monthly' || currentView === 'weekly') ? (
                        <button 
                          onClick={() => startEditing(task)}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Edit"
                        >
                          <Edit3 className="w-3 h-3 text-gray-600" />
                        </button>
                      ) : (
                        <button 
                          className="p-1 bg-gray-100 rounded cursor-not-allowed"
                          title="Editing disabled in Master view"
                          disabled
                        >
                          <Edit3 className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {searchedTasks.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search term.' : 'No tasks match the current filters.'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen w-full bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">EKAM Project</h1>
            <p className="text-gray-600">Construction Planning Management</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Plan Level Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setCurrentView('master')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentView === 'master' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Master Plan ({masterPlan.length})
                <div className="text-xs opacity-75">Original Schedule</div>
              </button>
              <button
                onClick={() => setCurrentView('monthly')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentView === 'monthly' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly Plan ({monthlyPlan.length})
                <div className="text-xs opacity-75">Set Monthly Targets</div>
              </button>
              <button
                onClick={() => setCurrentView('weekly')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentView === 'weekly' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Weekly Plan ({weeklyPlan.length})
                <div className="text-xs opacity-75">Detailed Execution</div>
              </button>
            </div>
            
            {/* Progress Panel Toggle */}
            <button 
              onClick={() => setShowProgressPanel(!showProgressPanel)}
              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="Toggle Progress Panel"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Progress</span>
              {showProgressPanel ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Tree Navigation */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4">
            {/* Root node */}
            <div 
              className={`flex items-center space-x-2 py-2 px-3 hover:bg-gray-100 cursor-pointer rounded-lg mb-2 ${
                selectedNode === 'root' ? 'bg-blue-50 border-l-4 border-blue-500' : ''
              }`}
              onClick={() => selectNode('root', ['All Buildings'])}
            >
              <FileText className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-sm text-gray-900">All Buildings</span>
              <span className="text-xs text-gray-500 ml-2">
                ({getCurrentTasks().length})
              </span>
            </div>

            {Object.entries(treeStructure).map(([key, node]) => (
              <TreeNode key={key} nodeKey={key} node={node} level={0} parentPath={['All Buildings']} />
            ))}
          </div>
        </div>

        {/* Right Panel - Filtered Task Table */}
        <div className="flex-1 bg-white flex flex-col">
          <TaskTable />
        </div>
      </div>

      {/* Right Slide-in Progress Panel */}
      <div className={`fixed top-0 right-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out z-50 ${
        showProgressPanel ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Construction Progress</h3>
            </div>
            <button 
              onClick={() => setShowProgressPanel(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Overall Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">🏗️ C Building Overview</h4>
                <span className="text-lg font-bold text-blue-600">23% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div className="bg-blue-600 h-3 rounded-full" style={{ width: '23%' }}></div>
              </div>
              <div className="text-xs text-gray-500">
                Updated: {new Date().toLocaleDateString()} • 5 tasks active
              </div>
            </div>

            {/* Stage Progress */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Construction Stages</h4>
              <div className="space-y-3">
                {Object.entries(CONSTRUCTION_STAGES).map(([stage, info]) => {
                  const stageProgress = Math.random() * 100; // Mock progress - replace with real calculation
                  const isActive = stageProgress > 0 && stageProgress < 100;
                  const isCompleted = stageProgress >= 100;
                  const taskCount = Math.floor(Math.random() * 200) + 1;
                  
                  return (
                    <div key={stage} className="border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${info.color}`}>
                            #{info.order}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{stage}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-700">
                          {Math.floor(stageProgress)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className={`h-2 rounded-full ${
                            isCompleted ? 'bg-green-500' : 
                            isActive ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          style={{ width: `${Math.min(stageProgress, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{taskCount} tasks</span>
                        <span className={`px-1 py-0.5 rounded ${
                          isCompleted ? 'bg-green-100 text-green-700' :
                          isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isCompleted ? 'Completed' : isActive ? 'In Progress' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Critical Issues */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">🚨 Critical Issues</h4>
              <div className="space-y-2">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-sm font-medium text-red-800">3 RCC tasks delaying Masonry</span>
                  </div>
                  <p className="text-xs text-red-600 ml-4">Expected delay: 5-7 days</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    <span className="text-sm font-medium text-yellow-800">Material shortage - Waterproofing</span>
                  </div>
                  <p className="text-xs text-yellow-600 ml-4">Delivery expected: Mar 20</p>
                </div>
              </div>
            </div>

            {/* Current Focus */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">🎯 Current Focus</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm font-medium text-blue-900 mb-1">RCC Stage</div>
                <div className="text-xs text-blue-700 mb-2">125 active tasks • 65% complete</div>
                <div className="text-xs text-gray-600">
                  <div><strong>Next:</strong> Masonry (Est. start: Mar 15)</div>
                  <div><strong>Priority:</strong> Complete foundation work</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {showProgressPanel && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={() => setShowProgressPanel(false)}
        ></div>
      )}
    </div>
  );
};

export default ConstructionPlanningInterface;
